import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Deca - a cool systems programming language",
  "description": "",
  "published": "2012-01-04T00:00:00.000Z",
  "redirect_from": ["/blog/deca-a-cool-systems-programming-language/swizec/3323"],
  "categories": "Bjarne Stroustrup, Deca, Eli Gottlieb, High-level programming language, Java, Object-oriented programming, Pointer (computing), Type system, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/01/4972036859_feeca3b936_m8.jpg",
        "alt": "DeCA partners with military to help promote su...",
        "title": "DeCA partners with military to help promote su..."
      }}></img></p>
    <p>{`This post is a summary of Eli Gottlieb's thesis on the `}<a parentName="p" {...{
        "href": "http://decac.googlecode.com/files/Deca%20Thesis.pdf"
      }}>{`Deca programming language`}</a>{` from May 2011. In short `}<em parentName="p">{`Deca is a language designed to provide the advanced features of sophisticated, `}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/High-level_programming_language",
          "title": "High-level programming language"
        }}>{`high-level programming languages`}</a>{`while still programming as close as possible to the bare metal. It brings in the functional, object-oriented and generic programming paradigms without requiring a garbage collector or a threading system.`}</em></p>
    <blockquote>
      <p parentName="blockquote">{`Since it is a programming-language thesis, it is also dedicated to every programmer who ever wanted a better language but could not use a virtual machine or a run-time library. To them I dedicate this thesis and say: `}<em parentName="p">{`I am become type theory, destroyer of minds.`}</em></p>
    </blockquote>
    <h2 {...{
      "id": "problems-of-systems-programming"
    }}>{`Problems of systems programming`}</h2>
    <p>{`I once contemplated doing some Linux kernel hacking, but decided to lie down until the feeling passes. That is my closest brush with systems programming and after reading this thesis - yikes.`}</p>
    <p>{`Essentially the problems are what you'd expect when dealing with hardware-imposed limitations, lacking useful abstractions and safety features - in fact, you're usually the one creating these.`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`precise data representation`}</strong>{` - working so close to bare metal means datatypes must correspond directly to their hardware representations. You can't just have a magical List datatype`}<em parentName="li">{`,`}</em>{` you `}<em parentName="li">{`can`}</em>{` have a block of memory though`}</li>
      <li parentName="ol"><strong parentName="li">{`safety properties and confined unsafety `}</strong>{`- the most common form of safety is`}<em parentName="li">{`[`}{`caption id="" align="alignleft" width="300" caption="Panic"]`}<a parentName="em" {...{
            "href": "http://commons.wikipedia.org/wiki/File:Kernel-panic.jpg"
          }}><img parentName="a" {...{
              "src": "https://swizec.com/blog/wp-content/uploads/2012/01/300px-Kernel-panic6.jpg",
              "alt": "English: Kernel panic Magyar: \"Kernel pan...",
              "title": "English: Kernel panic Magyar: \"Kernel pan..."
            }}></img></a>{`[`}{`/caption]`}<a parentName="em" {...{
            "href": "http://en.wikipedia.org/wiki/Type_safety",
            "title": "Type safety"
          }}>{`type safety`}</a>{`, `}{`_`}{`the idea being that the compiler makes sure you aren't trying to multiply a sack of potates with a banana. But other safety features a good language should provide according to Gottlieb are `}</em><a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Escape_analysis"
        }}>{`escape analysis`}</a><em parentName="li">{` for pointers, `}</em><a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Region-based_memory_management",
          "title": "Region-based memory management"
        }}>{`region-based memory management`}</a><em parentName="li">{` and`}</em>{` `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Type_safety#Definitions"
        }}>{`preservation and progress`}</a>{`_`}{` (well-typedness)`}</li>
      <li parentName="ol"><strong parentName="li">{`abstraction, encapsulation, modularity `}</strong>{`- most modern languages provide ways of packaging code so it can be reused and encapsulated. For instance: when you are using a stack structure, you don't really care whether it's implemented as a list or a memory vector.`}</li>
      <li parentName="ol"><strong parentName="li">{`extensibility `}</strong>{`- a way to extend the language itself with new features (for example, making the + operator work with new data types). So far possible solutions for this exist as `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Object-oriented_programming",
          "title": "Object-oriented programming"
        }}>{`OOP`}</a>{`, ad-hoc polymorphism, macros and so on, but it remains an open question and the perfect solution might not even exist`}</li>
      <li parentName="ol"><strong parentName="li">{`Stroustroup's rule `}</strong>{`- the lead designer of C++ `}<a parentName="li" {...{
          "href": "http://www.research.att.com/~bs/homepage.html",
          "title": "Bjarne Stroustrup"
        }}>{`Bjarne Stroustrup`}</a>{` once presented a rule that `}<em parentName="li">{`What you don't use, you don't pay for.`}</em>{` But in many high-level languages automatic memory management runs whether you need it or not, or every function needs exception handling ... all big problems in the limited confines of bare metal programming.`}</li>
    </ol>
    <h2 {...{
      "id": "decas-solutions"
    }}>{`Deca's solutions`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/01/300px-Safety6.jpg",
        "alt": "English: Safety",
        "title": "English: Safety"
      }}></img></p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`unboxed data types `}</strong>{`- for those of us who didn't know, `}<a parentName="li" {...{
          "href": "http://www.haskell.org/ghc/docs/latest/html/users_guide/primitives.html"
        }}>{`boxed types`}</a>{` are represented by a point to an object; in Deca all types are compiled down to raw unboxed representations - just a value - so when the code is running there's no more overhead. There are also two kinds of pointers (scoped and referenced) that allow you to use a pointer as if it was a variable, which sounds pretty cool from my experience with explicit pointers.`}</li>
      <li parentName="ol"><strong parentName="li">{`type safety `}</strong>{`- admittedly, this section went a bit over my head, but Deca uses a magical combination of static type inference (static typing where the compiler guesses stuff for you) and bit-casting - this is a system that allows you to eschew type safety under certain conditions because systems programmers apparently need that. If you care for this sort of thing -> Deca uses a modified `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Type_inference",
          "title": "Type inference"
        }}>{`Hindley-Milner`}</a>{` inference algorithm that also allows subtyping`}</li>
      <li parentName="ol"><strong parentName="li">{`module system `}</strong>{`- just as you'd expect of any modern language, you can package things into modules and modules into modules`}</li>
      <li parentName="ol"><strong parentName="li">{`encapsulated `}<a parentName="strong" {...{
            "href": "http://en.wikipedia.org/wiki/Type_system",
            "title": "Type system"
          }}>{`existential types`}</a>{` `}</strong>{`- these are best known as the type-theoretic encoding of abstract data types - giving us the ability to use data structures without knowing all the internal logic. In Deca these exist as a language extension and the whole thing works out just like it did for Caml`}</li>
      <li parentName="ol"><strong parentName="li">{`extensible types `}</strong>{`- Deca provides two ways of extending data types. The internal way of "open-sum variant types", which I don't understand and the thesis isn't very specific as to what thi means. The other are good old friendly classes, which we all understand and love from object-oriented languages`}</li>
      <li parentName="ol"><strong parentName="li">{`symmetric multiple dispatch `}</strong>{`- dynamic dispatch is a way to dynamically decide which method to call in order to process a particular message (polymorphism, pattern matching etc.) Deca does this by having a partially ordered list of possible methods, walking through it and when it finds something that can execute the given arguments, it is the most specific binding.`}</li>
      <li parentName="ol"><strong parentName="li">{`low-level encodings of high-level features `}</strong>{`- this section of the thesis is a bit longer, but it essentially boils down to the idea of using the `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/LLVM"
        }}>{`LLVM`}</a>{` to run compiled code and making sure all the features explained above are compiled to their most basic incarnations. According to another section of the thesis this also ensures adherence to Stroustrup's rule`}</li>
    </ol>
    <h2 {...{
      "id": "an-example"
    }}>{`An example`}</h2>
    <p>{`I would love to personally produce an example of what Deca looks like, but I'm already having enough `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/learning-me-a-haskell/swizec/3272",
        "title": "Learning me a Haskell"
      }}>{`fun learning Haskell`}</a>{`, so here's an official example of a List implementation.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-deca"
      }}>{`module list

import malloc

type List<a> = class(e,n) extends Sequence</a><a> {
  element: a:= e;
  next: @List:= n;
}

function cons(element,next) {
  malloc.malloc(pool => new(pool)(List(element,next)))
}

function car(lst: @List) {
  match *lst {
    case Cons(head,tail) => Some(head)
    case Nil => None
  }
}

function cdr(lst: @List) {
  match *lst {
    case Cons(head,tail) => tail
    case Nil => Nil
  }
}

end

</a>
`}</code></pre>
    <h2 {...{
      "id": "conclusion"
    }}><a parentName="h2" {...{
        "href": ""
      }}>{`Conclusion`}</a></h2>
    <p><a parentName="p" {...{
        "href": ""
      }}>{`The thesis itself also compares Deca to other modern high-level languages for systems programming like `}</a><a parentName="p" {...{
        "href": "http://lambda-the-ultimate.org/node/4023"
      }}>{`Clay`}</a>{`, `}<a parentName="p" {...{
        "href": "http://www.bitc-lang.org/",
        "title": "BitC"
      }}>{`BitC`}</a>{`, `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Cyclone_programming_language"
      }}>{`Cyclone`}</a>{` and Java "with magic". That section didn't feel too important, the resolution is simply that Deca is `}<em parentName="p">{`better`}</em>{`.`}</p>
    <p>{`Unfortunately though, Deca itself doesn't look to be ready for real-world use just yet. Even though the language itself is pretty much defined and its grammar is known, no complete compiler yet exists. The `}<a parentName="p" {...{
        "href": "http://code.google.com/p/decac/"
      }}>{`official compiler, decac,`}</a>{` developed by Gottlieb doesn't yet support all the features and I've heard rumors it has been scrapped and is being developed anew because some fundamental issues were discovered.`}</p>
    <p>{`All in all, this looks like an interesting language to keep an eye on if you're a systems programmer, but I feel C will be the king for a long while yet.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://americannationnews.wordpress.com/2012/01/02/are-you-a-programming-freek/"
        }}>{`Are you a programming freek?`}</a>{` (americannationnews.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://wiki.answers.com/Q/What_is_meant_by_high_level_language_in_IT"
        }}>{`What is meant by high level language in IT`}</a>{` (wiki.answers.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8697033/is-it-possible-to-make-a-high-level-compiled-language"
        }}>{`Is it possible to make a high level compiled language?`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://wiki.answers.com/Q/What_are_different_application_areas_of_programming_language"
        }}>{`What are different application areas of programming language`}</a>{` (wiki.answers.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=da758ab3-ef49-445c-82e5-d0740a42b9a2",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      